/**
 * Monthly financial view. Workbook equivalents (docs/workbook-analysis.md §9):
 *   income.total = E8 · expenses.total = J4 · budget.expenses = J3 · budget.variance = J5
 *   savings.actual = J7 (E8 − J4) · savings.projected = J6 (E5 − J3; E5 convention for every month)
 */
import type { IncomeType } from "@/domain/income";
import { workbookIncomeLine } from "@/domain/income";
import type { Expense, Income, YearMonth } from "@/domain/model";
import { Decimal, sum, toDecimalString, type DecimalString } from "@/domain/money";
import { balanceAt, type BalanceRow } from "./balance";
import { categoryBreakdown, monthBudgetTotal, natureSplit, type GroupLine, type NatureSplit } from "./budget";
import { INCOME_TYPE_ORDER, type FinanceIndex } from "./data";

export interface MonthReport {
  month: YearMonth;
  hasData: boolean;
  income: {
    total: DecimalString;
    /** Workbook line "Stipendio" (E6) */
    salary: DecimalString;
    /** Workbook line "Extra Entrate" (E7) */
    extra: DecimalString;
    byType: Record<IncomeType, DecimalString>;
    entries: Income[];
  };
  expenses: {
    total: DecimalString;
    count: number;
    average: DecimalString | null;
    byGroup: GroupLine[];
    byNature: NatureSplit;
    largest: Expense[];
  };
  budget: {
    /** Σ category budgets (J3) */
    expenses: DecimalString;
    incomeSalary: DecimalString;
    incomeExtra: DecimalString;
    /** Budgeted income (E5) */
    income: DecimalString;
    /** budget − actual expenses (J5) */
    variance: DecimalString;
    /** max(budget − actual, 0) */
    remaining: DecimalString;
    /** actual / budget */
    usedRatio: DecimalString | null;
    hasBudget: boolean;
  };
  savings: {
    /** income − expenses (J7) */
    actual: DecimalString;
    /** savings / income, null without income */
    rate: DecimalString | null;
    /** budgeted income − budget (J6) */
    projected: DecimalString;
    /** actual − projected (J8) */
    vsProjected: DecimalString;
  };
  balance: BalanceRow;
}

export function computeMonth(index: FinanceIndex, month: YearMonth, { largestCount = 5 } = {}): MonthReport {
  const incomes = [...(index.incomesByMonth.get(month) ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const expenses = index.expensesByMonth.get(month) ?? [];

  const byType = Object.fromEntries(INCOME_TYPE_ORDER.map((t) => [t, new Decimal(0)])) as Record<IncomeType, Decimal>;
  let salary = new Decimal(0);
  let extra = new Decimal(0);
  for (const i of incomes) {
    byType[i.type] = byType[i.type].plus(i.amount);
    if (workbookIncomeLine(i.type) === "salary") salary = salary.plus(i.amount);
    else extra = extra.plus(i.amount);
  }
  const incomeTotal = salary.plus(extra);
  const expenseTotal = sum(expenses.map((e) => e.amountEur));

  const incomeBudget = index.incomeBudgets.get(month);
  const budgetSalary = new Decimal(incomeBudget?.salary ?? 0);
  const budgetExtra = new Decimal(incomeBudget?.extra ?? 0);
  const budgetIncome = budgetSalary.plus(budgetExtra);
  const budgetExpenses = monthBudgetTotal(index, month);
  const hasBudget = index.budgetsByMonth.has(month) || incomeBudget !== undefined;

  const savings = incomeTotal.minus(expenseTotal);
  const projected = budgetIncome.minus(budgetExpenses);
  const variance = budgetExpenses.minus(expenseTotal);

  const largest = [...expenses]
    .sort((a, b) => new Decimal(b.amountEur).cmp(a.amountEur) || b.purchaseDate.localeCompare(a.purchaseDate))
    .slice(0, largestCount);

  return {
    month,
    hasData: incomes.length > 0 || expenses.length > 0,
    income: {
      total: toDecimalString(incomeTotal),
      salary: toDecimalString(salary),
      extra: toDecimalString(extra),
      byType: Object.fromEntries(INCOME_TYPE_ORDER.map((t) => [t, toDecimalString(byType[t])])) as Record<IncomeType, DecimalString>,
      entries: incomes,
    },
    expenses: {
      total: toDecimalString(expenseTotal),
      count: expenses.length,
      average: expenses.length ? toDecimalString(expenseTotal.div(expenses.length)) : null,
      byGroup: categoryBreakdown(index, [month]),
      byNature: natureSplit(index, [month]),
      largest,
    },
    budget: {
      expenses: toDecimalString(budgetExpenses),
      incomeSalary: toDecimalString(budgetSalary),
      incomeExtra: toDecimalString(budgetExtra),
      income: toDecimalString(budgetIncome),
      variance: toDecimalString(variance),
      remaining: toDecimalString(Decimal.max(variance, 0)),
      usedRatio: budgetExpenses.gt(0) ? toDecimalString(expenseTotal.div(budgetExpenses)) : null,
      hasBudget,
    },
    savings: {
      actual: toDecimalString(savings),
      rate: incomeTotal.gt(0) ? toDecimalString(savings.div(incomeTotal)) : null,
      projected: toDecimalString(projected),
      vsProjected: toDecimalString(savings.minus(projected)),
    },
    balance: balanceAt(index, month),
  };
}
